import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Row} from 'react-table';
import {toast} from 'react-toastify';

import {validateCodesByArray} from 'api';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {QRScannerModal} from 'components/ui-kit/ScanCode';

import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';

import {IS_NG} from 'core/constants/env';

import {$allowedActionsForInvoice, $doesInvoiceExist} from 'models/documents/invoices/common';
import {
    $initalLineValuesHolder,
    $invoiceCreationPage,
    $invoiceLineChargeData,
    $invoiceLinesErrors,
    $invoiceLinesHolder,
    $invoiceLinesHolderCodesTotal,
    $invoiceLineValues,
    addInvoiceLineValues,
    invoiceCreationPage,
    setInitalLineValuesHolder,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';

import {
    $modalTransportCodesModal,
    setCurrentScannedGtin,
    setModalTransportCodesModal,
} from 'models/documents/invoices/details';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes} from 'types/gtins';
import {InvocieLineHolder, InvoiceLine} from 'types/invoices';
import {CodeResponseType} from 'types/validation';

import {ControlSection} from '../../ControlSection';

import {VAT_CATEGORY} from '../../helpers/consts';
import {useCodeScanner} from '../../hooks/useCodeScanner';
import {useCombinedData2} from '../../hooks/useCombinedData2';

import {useInvoiceLinesColumns} from '../../hooks/useInvoiceLinesColumns';
import {useLineCalculations} from '../../hooks/useLineCalculations';
import {
    ParsedDataExpected,
    XlsxFileInvalidRow,
    XlsxFileParsedData,
} from '../../hooks/useParseInvoiceLinesXlsx';

import {InvoiceLineTypeBanner} from './components/InvoiceLineTypeBanner';
import {ModalDuplicateLines} from './components/ModalDuplicateLines';
import {ModalFormComponent, ModalFormReadonly} from './components/ModalForm';
import {ModalGroupByGtin} from './components/ModalGroupByGtin';
import {ModalParseLinesFromXlsx} from './components/ModalParseLinesFromXlsx';
import {ModalTransportCodes} from './components/ModalTransportCodes';
import {
    SimpleTable,
    SimpleTablePagination,
    PaginationControlsProps,
} from './components/SimpleTable';
import {
    AddInvoiceLineForm,
    ButtonGroup,
    Container,
    Headline,
    Highlighted,
    Root,
    SectionDescription,
} from './styled';
import {createValidationContext, lineSchema} from './validation';

interface InvoiceLineData extends ParsedDataExpected {
    serviceCategory: {label: string; value: string};
    gtin: string;
    identityCode: string;
    productName: string;
    packagingType: PackageTypes;
}

export const processInvoiceLinesWithCombinedCodes = (lines: InvocieLineHolder[]): InvoiceLine[] => {
    const res = processLinesRecursive(lines, undefined, 1);
    return res;
};

function processLinesRecursive(
    lines: InvocieLineHolder[],
    parentCode: string | undefined,
    depth: number,
): InvoiceLine[] {
    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
    const result: InvoiceLine[] = [];

    for (const line of lines) {
        const addCharges = line.charges
            ? toLowerAndTrim(line.charges.isCharge) === 'true'
                ? Number(line.charges.amountXaf)
                : -Number(line.charges.amountXaf)
            : 0;

        const qtyNum = 1;
        const lineExtension = qtyNum * (Number(line.price) || 0) + addCharges;
        const vatRate = Number(line.vat) || 0;
        const lineAmountWVatNum = (vatRate / 100) * lineExtension + lineExtension;

        const baseMeta = {level: depth, parent: parentCode};
        const linesStr = lineExtension.toFixed(2);
        const lineAmountWVatStr = lineAmountWVatNum.toFixed(2);

        if (!line.combinedCodes || line.combinedCodes.length === 0) {
            result.push({
                ...line,
                qty: line.qty ?? '1',
                lines: line.lines ?? linesStr,
                lineAmountWVat: line.lineAmountWVat ?? lineAmountWVatStr,
                ...baseMeta,
            } as InvoiceLine);

            if (line.subRows?.length) {
                const childParentCode = line.code ?? parentCode;
                result.push(...processLinesRecursive(line.subRows, childParentCode, depth + 1));
            }
            continue;
        }

        const index = `row-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`;

        const {combinedCodes, subRows, ...baseLine} = line;
        const [firstCode, ...remainingCodes] = combinedCodes;
        const childParentCode = firstCode ?? line.code ?? parentCode;

        result.push({
            ...baseLine,
            code: firstCode,
            qty: '1',
            lines: linesStr,
            lineAmountWVat: lineAmountWVatStr,
            ...(remainingCodes.length > 0 && {combinedCodeId: index}),
            ...baseMeta,
        } as InvoiceLine);

        for (const code of remainingCodes) {
            result.push({
                ...baseLine,
                code,
                qty: '1',
                lines: linesStr,
                lineAmountWVat: lineAmountWVatStr,
                combinedCodeId: index,
                ...baseMeta,
            } as InvoiceLine);
        }

        if (subRows?.length) {
            result.push(...processLinesRecursive(subRows, childParentCode, depth + 1));
        }
        // index++;
    }

    return result;
}

export const InvoiceLinesTab = () => {
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const currentPageIndex = useStore($invoiceCreationPage);
    const invoiceLineValues = useStore($invoiceLineValues);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const invoiceLineChargeData = useStore($invoiceLineChargeData);
    const initalLineValuesHolder = useStore($initalLineValuesHolder);
    const [allowSubmitButton, setAllowSubmitButton] = useState<boolean>(false);
    const [xlsxErrors, setXlsxErrors] = useState<XlsxFileInvalidRow>({});
    const [combineByGtin, setCombineByGtin] = useState(false);
    const [dataParsingPending, setDataParsingPending] = useState(false);
    const invoiceLinesHolderCodesTotal = useStore($invoiceLinesHolderCodesTotal);
    const modalTransportCodesModal = useStore($modalTransportCodesModal);
    const [lineType, setLineType] = useState<'unit' | 'transport' | null>(null);

    const [paginationProps, setPaginationProps] = useState<PaginationControlsProps | null>(null);

    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const [scanShow, setScanShow] = React.useState(false);
    const [scanPending, setScanPending] = React.useState(false);

    const resetCombineByGtin = useCallback(() => {
        setCurrentScannedGtin(null);
        setCombineByGtin(false);
    }, []);

    useEffect(() => {
        if (!combineByGtin) {
            setCurrentScannedGtin(null);
        }
    }, [combineByGtin]);

    const convertScanToInvoiceView = (
        scan: CodeResponseType['info'],
    ): Partial<InvoiceLine & {codeGtin: string}> => ({
        code: scan.identityCode,
        gtin: scan.gtin,
        qty: '1',
        description: scan.productName,
        codeGtin: scan.identityCode,

        price: '',
        lines: '',
        vatCategory: {label: '', value: ''},
        vat: '',
        lineAmountWVat: '',
        isHsn: 'HS',
        serviceCategory: {label: scan.hsnDescription ?? scan.hsnCode, value: scan.hsnCode},
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: true,
            allowToDelete: true,
        },
        isMarkedItem: true,
    });

    const DEFAULT_LINE_VALUES: Partial<InvoiceLine> = {
        description: '',
        sellersItemId: '',
        buyersItemId: '',
        code: '',
        gtin: '',
        qty: '',
        isHsn: 'HS',
        serviceCategory: {label: '', value: ''},
        isMarkedItem: false,
        packagingType: undefined,
        level: 1,
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: false,
            allowToDelete: true,
        },
    };

    const handleEnterFromLine = (response: CodeResponseType['info']) => {
        if (response.packagingType && !CUSTOMER_PACKAGE_TYPES.includes(response.packagingType)) {
            setLineType('transport');
            setInitalLineValuesHolder(response);
        } else {
            setLineType('unit');

            const {description, code, gtin, serviceCategory} = convertScanToInvoiceView(response);
            setInitalLineValuesHolder({
                ...DEFAULT_LINE_VALUES,
                ...(code &&
                    gtin && {
                        qty: '1',
                        description,
                        packagingType: response.packagingType,
                        code,
                        gtin,
                        serviceCategory,
                        isMarkedItem: true,
                        copyActions: {
                            allowToCopy: false,
                            allowToDuplicateDown: false,
                            allowToPaste: false,
                            allowToDelete: true,
                        },
                    }),
            });

            setValue('description', description);
            setValue('qty', '1');
            setValue('isHsn', 'HS');
            setValue('serviceCategory', serviceCategory);
        }

        return false;
    };

    const clearCodeAndDescription2 = () => {
        setValue('codeGtin', '');
        setValue('description', '');
        setValue('serviceCategory', '');
        setValue('sellersItemId', '');
        setValue('buyersItemId', '');
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
        setLineType(null);
    };

    const {onEnterCode, codeGtinHolder} = useCodeScanner({
        combineByGtin,
        handleEnterFromLine,
        clearCodeAndDescription: clearCodeAndDescription2,
        setScanPending,
    });

    const formContext = useMemo(() => createValidationContext({lineType}), [lineType]);

    const {
        control,
        handleSubmit,
        trigger,
        setValue,
        getValues,
        reset,
        formState: {errors},
    } = useForm({
        defaultValues: {
            description: '',
            sellersItemId: '',
            buyersItemId: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: null,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
            invoiceLineType: 'Unmarked',
        },
        resolver: yupResolver(lineSchema),
        context: formContext,
    });

    useEffect(() => {
        if (lineType === 'transport') {
            trigger(['qty', 'price', 'vatCategory', 'vat']).catch(() => {});
        }
    }, [lineType, trigger]);

    const onSubmit = (data: any) => {
        const isDouble = initalLineValuesHolder?.code
            ? $invoiceLinesHolderCodesTotal.getState().includes(initalLineValuesHolder.code)
            : false;

        if (isDouble) {
            toast.error(
                t('xlsxModal.errors.markingCodeExists', {
                    markingCode: initalLineValuesHolder?.code,
                }),
            );
            return;
        }

        if (
            initalLineValuesHolder?.packagingType &&
            !CUSTOMER_PACKAGE_TYPES.includes(initalLineValuesHolder.packagingType)
        ) {
            setInvoiceLinesHolder([initalLineValuesHolder as InvocieLineHolder]);
        } else {
            setInvoiceLinesHolder([
                {
                    ...data,
                    packagingType: initalLineValuesHolder?.packagingType ?? '',
                    isMarkedItem: !initalLineValuesHolder
                        ? false
                        : initalLineValuesHolder.isMarkedItem,
                    copyActions: initalLineValuesHolder
                        ? initalLineValuesHolder?.copyActions
                        : {
                              allowToCopy: true,
                              allowToDuplicateDown: false,
                              allowToPaste: false,
                              allowToDelete: true,
                          },
                    ...(invoiceLineChargeData && {charges: invoiceLineChargeData}),
                    ...(initalLineValuesHolder && {
                        code: initalLineValuesHolder.code,
                        gtin: initalLineValuesHolder.gtin,
                    }),
                },
            ]);
        }

        setLineType(null);
        const invoiceLineTypeValue = getValues('invoiceLineType');
        reset({
            description: '',
            sellersItemId: '',
            buyersItemId: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: null,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
            invoiceLineType: invoiceLineTypeValue ?? 'Unmarked',
        });
        codeGtinHolder.current = '';
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
        setInvoiceLineChargeData(null);
    };

    const {columnsSettings} = useInvoiceLinesColumns({
        control,
        getValues,
        isMarkedItem: false,
        clearCodeAndDescription2,
        allowToEdit: allowedActionsForInvoice.edit,
    });
    const columns = useMemo(() => mapTableColumns(columnsSettings), [columnsSettings]);

    const {mappedFields2} = useCombinedData2({
        control,
        t: translation,
        onEnterCode,
        trigger,
        getValues,
        setValue,
        errors,
        lineType,
    });

    const [skipPageReset, setSkipPageReset] = useState(true);

    const {combineValues, calculateLineAmountWVat} = useLineCalculations();
    const updateMyData = (
        columnId: string,
        value: string | object | null,
        depth: {step: number; id: string},
    ) => {
        setSkipPageReset(true);
        const {step, id} = depth;
        updateInvoiceLinesHolder({
            data: {columnId, value: value as any},
            depth: {step, id},
        });
        if (columnId === 'price' || columnId === 'qty') {
            combineValues(id);
            calculateLineAmountWVat(id);
        }
        if (columnId === 'vatCategory' || columnId === 'vat') {
            calculateLineAmountWVat(id, columnId);
        }
    };

    useEffect(() => {
        if (modalTransportCodesModal.show) {
            setSkipPageReset(true);
            return;
        }
        setSkipPageReset(true);
    }, [invoiceLinesHolder, modalTransportCodesModal]);

    const mappedData = useMemo(() => invoiceLinesHolder, [invoiceLinesHolder]);

    const [showXlsxModal, setShowXlsxModal] = useState(false);
    const onCloseXlsxModal = () => {
        setShowXlsxModal(false);
        resetCombineByGtin();
    };
    function onShowXlsxModal() {
        setShowXlsxModal(true);
    }

    const datToStore = (data: InvoiceLineData[]) =>
        data.map((item, index) => {
            const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
            const {amount, baseAmount, charges, rate, reason, price, vat, packagingType} = item;

            const addCharges =
                toLowerAndTrim(charges) === 'charge' ? Number(amount) : -Number(amount);
            const lines = Number(price) + addCharges;
            const lineAmountWVat = (Number(vat) / 100) * lines + lines;
            return {
                isMarkedItem: true,
                copyActions: {
                    allowToCopy: true,
                    allowToDuplicateDown: false,
                    allowToPaste: false,
                    allowToDelete: true,
                },
                code: item.identityCode,
                gtin: item.gtin,
                qty: '1',
                description: item.productName,
                packagingType,
                price: item?.price?.toString(),
                lines: lines.toFixed(2),
                vatCategory: VAT_CATEGORY.find((i) => i.value === item?.category) ?? {
                    label: '',
                    value: '',
                },
                vat: item?.vat?.toString(),
                lineAmountWVat: lineAmountWVat.toFixed(2),
                level: 1,
                isHsn: 'HS',
                serviceCategory: item.serviceCategory ? item.serviceCategory : undefined,
                ...(!!charges && {
                    charges: {
                        discountRateAndBaseAmount: !!(baseAmount && rate),
                        ...(!!(baseAmount && rate) && {baseAmount: Number(baseAmount)}),
                        ...(!!(baseAmount && rate) && {ratePercent: Number(rate)}),
                        amountXaf: Number(amount),
                        isCharge: toLowerAndTrim(charges) === 'charge' ? 'true' : 'false',
                        reason: reason ?? '',
                    },
                }),
            };
        });

    async function onDataParsed(data: XlsxFileParsedData) {
        if (!data || !data.data) {
            return;
        }

        try {
            setDataParsingPending(true);
            const response = await validateCodesByArray(
                data.data?.map((item) => item?.markingCode ?? '') ?? [],
            );

            const errors = Object.entries(response.data.errors);
            const errorsIndexes = errors.reduce((acc, [index, error]) => {
                acc[index] = {
                    error: [
                        error.map((e: {code: string; message: string}) => e.message).join(', '),
                    ],
                };
                return acc;
            }, {} as XlsxFileInvalidRow);
            if (Object.keys(errorsIndexes).length > 0) {
                setXlsxErrors(errorsIndexes);
                return;
            }

            if (combineByGtin) {
                const responses = response.data.data.map((item) => item.info);
                const fullfilledResponsesWithGroup = responses.reduce((acc, item, index) => {
                    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
                    const {
                        gtin,
                        identityCode,
                        productName,
                        hsnCode,
                        hsnDescription,
                        packagingType,
                    } = item;

                    const {
                        amount,
                        baseAmount,
                        charges,
                        rate,
                        reason,
                        price,
                        vat,
                        category,
                    } = (data.data as ParsedDataExpected[])[index];

                    const hashKeyObject = {
                        gtin,
                        price,
                        charges:
                            typeof charges === 'string'
                                ? charges.toLowerCase()
                                : JSON.stringify(charges).toLowerCase(),
                        rate,
                        reason,
                        vat,
                        category,
                    };

                    const hashKey = JSON.stringify(hashKeyObject);

                    if (acc[hashKey]) {
                        const addCharges = acc[hashKey]['charges']
                            ? toLowerAndTrim(acc[hashKey]['charges'].isCharge) === 'true'
                                ? Number(acc[hashKey].charges.amountXaf)
                                : -Number(acc[hashKey].charges.amountXaf)
                            : 0;

                        const qty = Number(acc[hashKey]['qty']) + 1;
                        const lines = qty * Number(acc[hashKey]['price']) + addCharges;
                        const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                        acc[hashKey]['combinedCodes'] = [
                            ...acc[hashKey]['combinedCodes'],
                            identityCode,
                        ];
                        acc[hashKey]['qty'] = qty.toString();
                        acc[hashKey]['lines'] = lines.toFixed(2);
                        acc[hashKey]['lineAmountWVat'] = lineAmountWVat.toFixed(2);
                        return acc;
                    }

                    const addCharges = charges
                        ? toLowerAndTrim(charges) === 'charge'
                            ? Number(amount)
                            : -Number(amount)
                        : 0;
                    const lines = Number(price) + addCharges;
                    const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                    return {
                        ...acc,
                        [hashKey]: {
                            isMarkedItem: true,
                            copyActions: {
                                allowToCopy: true,
                                allowToDuplicateDown: false,
                                allowToPaste: false,
                                allowToDelete: true,
                            },
                            code: '',
                            gtin,
                            qty: '1',
                            description: productName,

                            price: price?.toString(),
                            lines: lines.toFixed(2),
                            vatCategory: VAT_CATEGORY.find((i) => i.value === category) ?? {
                                label: '',
                                value: '',
                            },
                            vat: vat?.toString(),
                            lineAmountWVat: lineAmountWVat.toFixed(2),
                            isHsn: 'HS',
                            serviceCategory: {
                                label: hsnDescription ?? hsnCode,
                                value: hsnCode,
                            },
                            packagingType,
                            combinedCodes: [identityCode],
                            ...(!!charges && {
                                charges: {
                                    discountRateAndBaseAmount: !!(baseAmount && rate),
                                    ...(!!(baseAmount && rate) && {baseAmount: Number(baseAmount)}),
                                    ...(!!(baseAmount && rate) && {ratePercent: Number(rate)}),
                                    amountXaf: Number(amount),
                                    isCharge:
                                        toLowerAndTrim(charges) === 'charge' ? 'true' : 'false',
                                    reason: reason ?? '',
                                },
                            }),
                        },
                    };
                }, {} as Record<string, any>);

                const dataToStore = Object.values(
                    fullfilledResponsesWithGroup,
                ) as InvocieLineHolder[];

                setInvoiceLinesHolder(dataToStore);
                onCloseXlsxModal();
                return;
            }

            const fullfilledResponses = response.data.data.map((res, index) => {
                const {
                    gtin,
                    identityCode,
                    productName,
                    hsnCode,
                    hsnDescription,
                    packagingType,
                } = res.info;
                const dummy = (data.data as ParsedDataExpected[])[index];
                return {
                    ...dummy,
                    gtin,
                    identityCode,
                    productName,
                    serviceCategory: {
                        label: hsnDescription ?? hsnCode,
                        value: hsnCode,
                    },
                    packagingType,
                };
            });

            const dataToStore = datToStore(fullfilledResponses) as InvocieLineHolder[];
            setInvoiceLinesHolder(dataToStore);
            onCloseXlsxModal();
        } catch (error) {
            console.error(error);
        } finally {
            setDataParsingPending(false);
        }
    }

    const onRowClick = (row: Row<object>) => {
        const payload = {
            show: true,
            id: row.id ? row.id.toString() : '',
        };
        setModalTransportCodesModal(payload);
    };

    return (
        <Root>
            <ModalFormComponent getValues={getValues} setValue={setValue} trigger={trigger} />
            <ModalFormReadonly />
            <ModalDuplicateLines />
            <ModalGroupByGtin />
            <ModalTransportCodes />
            <Container>
                <Headline>
                    <h3>
                        {t('invoiceLines')}
                        {allowedActionsForInvoice.edit && (
                            <Highlighted>
                                {`(${invoiceLinesHolderCodesTotal?.length ?? 0} ${t(
                                    'itemsAdded',
                                )})`}
                            </Highlighted>
                        )}
                    </h3>
                    <SectionDescription>
                        <span>{!allowedActionsForInvoice.edit ? '' : t('addGoods.1')}</span>
                    </SectionDescription>
                    <ButtonGroup style={IS_NG ? {justifyContent: 'flex-end'} : undefined}>
                        {allowedActionsForInvoice.edit && (
                            <>
                                {!IS_NG && (
                                    <Button
                                        variant={ButtonVariant.Outline}
                                        onClick={onShowXlsxModal}
                                    >
                                        {t('xlsxModal.upload')}
                                    </Button>
                                )}
                                <Button
                                    style={IS_NG ? {maxWidth: '156px'} : undefined}
                                    onClick={() => setScanShow(true)}
                                >
                                    {translation('components.scan.button')}
                                    <Icon source={ScanIcon} width={20} height={20} />
                                </Button>
                            </>
                        )}
                    </ButtonGroup>
                </Headline>
                <AddInvoiceLineForm onSubmit={handleSubmit(onSubmit)}>
                    {allowedActionsForInvoice.edit && (
                        <Table
                            hoverableRow={false}
                            tableViewExtraSettings={{
                                tdThPadding: '5px 10px',
                                overflowX: 'visible',
                            }}
                            cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                            hideSetting={true}
                            isLoading={false}
                            columns={columns}
                            data={mappedFields2}
                        />
                    )}

                    {allowedActionsForInvoice.edit && (
                        <>
                            <InvoiceLineTypeBanner type={lineType} />
                            <Button
                                style={{width: 'fit-content', position: 'sticky', left: 0}}
                                size="sm"
                                variant={ButtonVariant.Secondary}
                                disabled={allowSubmitButton || scanPending}
                                type="submit"
                            >
                                {translation(
                                    'pages.documents.invoices.create.buttons.addToInvoice',
                                )}
                            </Button>
                        </>
                    )}

                    <SimpleTable
                        hideHeaders={allowedActionsForInvoice.edit}
                        columns={columns}
                        data={mappedData}
                        onPaginationReady={setPaginationProps}
                        updateMyData={updateMyData}
                        skipPageReset={skipPageReset}
                        renderExpandedRow={true}
                        getRowCanExpand={(row) => {
                            const {packagingType} = row.original as InvocieLineHolder;
                            return packagingType === PackageTypes.BOX;
                        }}
                        // showExpandableColumn={false}
                        expandableColumnId="lineId"
                        onRowClick={onRowClick}
                        tableViewExtraSettings={{
                            tdThPadding: '5px 10px',
                            overflowX: 'visible',
                        }}
                        cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                    />
                </AddInvoiceLineForm>
                {paginationProps && mappedData.length > 0 && (
                    <div>
                        <SimpleTablePagination {...paginationProps} />
                    </div>
                )}
            </Container>
            <ControlSection
                nextIsDisabled={false}
                onSubmit={(nextIndex: number) => {
                    if (!invoiceLinesHolder.length) {
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <ul style={{margin: '6px 0'}}>
                                    <li>{t('errors.noLines')}</li>
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    if ($invoiceLinesErrors.getState().length) {
                        const limitErrors = [...$invoiceLinesErrors.getState()];
                        limitErrors.length > 5 ? (limitErrors.length = 5) : limitErrors;
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <strong>{t('errors.emptyFields')}</strong>
                                <ul style={{margin: '6px 0'}}>
                                    {limitErrors.map((e) => {
                                        const {index, fields} = e;
                                        return (
                                            <li key={index}>
                                                {t('errors.line')} {index}: {fields.join(', ')}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    const processedLines = processInvoiceLinesWithCombinedCodes(invoiceLinesHolder);

                    addInvoiceLineValues(processedLines);
                    invoiceCreationPage(nextIndex);
                }}
                nextIndex={currentPageIndex + 1}
            />
            <QRScannerModal
                className="qr-scanner-modal-invoices"
                open={scanShow}
                pending={scanPending}
                onClose={() => {
                    resetCombineByGtin();
                    setScanShow(false);
                }}
                onScan={onEnterCode}
                onManual={onEnterCode}
                allowToGroupByGtin={true}
                combineByGtin={combineByGtin}
                setCombineByGtin={setCombineByGtin}
            />
            <ModalParseLinesFromXlsx
                show={showXlsxModal}
                onClose={onCloseXlsxModal}
                onDataParsed={onDataParsed}
                xlsxErrors={xlsxErrors}
                setXlsxErrors={setXlsxErrors}
                combineByGtin={combineByGtin}
                setCombineByGtin={setCombineByGtin}
                dataParsingPending={dataParsingPending}
            />
        </Root>
    );
};
